import type { Bounds, Vec2 } from '@/types';

/** A visual alignment line in world space. */
export interface SnapGuide {
  readonly axis: 'x' | 'y';
  /** World coordinate of the line (x for vertical guides, y for horizontal). */
  readonly at: number;
  /** Extent along the other axis, covering both aligned bounds. */
  readonly from: number;
  readonly to: number;
  readonly kind: 'edge' | 'center' | 'grid';
}

export interface SnapOptions {
  /** World units; callers derive it from a screen tolerance divided by zoom. */
  readonly threshold: number;
  readonly gridSize: number;
  readonly snapToObjects: boolean;
  readonly snapToGrid: boolean;
}

export interface SnapResult {
  /** Correction to add to the proposed position. */
  readonly delta: Vec2;
  readonly guides: readonly SnapGuide[];
}

const NO_SNAP: SnapResult = { delta: { x: 0, y: 0 }, guides: [] };

interface Candidate {
  readonly diff: number;
  readonly at: number;
  readonly kind: 'edge' | 'center';
  readonly other: Bounds;
}

const linesX = (b: Bounds) => [
  { at: b.minX, kind: 'edge' as const },
  { at: (b.minX + b.maxX) / 2, kind: 'center' as const },
  { at: b.maxX, kind: 'edge' as const },
];
const linesY = (b: Bounds) => [
  { at: b.minY, kind: 'edge' as const },
  { at: (b.minY + b.maxY) / 2, kind: 'center' as const },
  { at: b.maxY, kind: 'edge' as const },
];

const best = (
  moving: readonly { at: number; kind: 'edge' | 'center' }[],
  others: readonly Bounds[],
  lines: (b: Bounds) => readonly { at: number; kind: 'edge' | 'center' }[],
  threshold: number,
): Candidate | null => {
  let found: Candidate | null = null;
  for (const other of others) {
    for (const target of lines(other)) {
      for (const own of moving) {
        const diff = target.at - own.at;
        if (Math.abs(diff) > threshold) continue;
        if (!found || Math.abs(diff) < Math.abs(found.diff)) found = { diff, at: target.at, kind: target.kind, other };
      }
    }
  }
  return found;
};

/**
 * Snaps a moving axis-aligned box against other boxes (edges and centres) and, when
 * no object candidate is within reach on an axis, against the grid. Pure and cheap:
 * callers pass only the boxes near the moving one. Each axis snaps independently.
 */
export const snapBounds = (moving: Bounds, others: readonly Bounds[], options: SnapOptions): SnapResult => {
  if (!options.snapToObjects && !options.snapToGrid) return NO_SNAP;
  const guides: SnapGuide[] = [];
  let dx = 0;
  let dy = 0;

  const cx = options.snapToObjects ? best(linesX(moving), others, linesX, options.threshold) : null;
  const cy = options.snapToObjects ? best(linesY(moving), others, linesY, options.threshold) : null;

  if (cx) {
    dx = cx.diff;
    guides.push({
      axis: 'x',
      at: cx.at,
      from: Math.min(moving.minY + dy, cx.other.minY),
      to: Math.max(moving.maxY + dy, cx.other.maxY),
      kind: cx.kind,
    });
  } else if (options.snapToGrid && options.gridSize > 0) {
    const snapped = Math.round(moving.minX / options.gridSize) * options.gridSize;
    if (Math.abs(snapped - moving.minX) <= options.threshold) dx = snapped - moving.minX;
  }

  if (cy) {
    dy = cy.diff;
    guides.push({
      axis: 'y',
      at: cy.at,
      from: Math.min(moving.minX + dx, cy.other.minX),
      to: Math.max(moving.maxX + dx, cy.other.maxX),
      kind: cy.kind,
    });
  } else if (options.snapToGrid && options.gridSize > 0) {
    const snapped = Math.round(moving.minY / options.gridSize) * options.gridSize;
    if (Math.abs(snapped - moving.minY) <= options.threshold) dy = snapped - moving.minY;
  }

  // Guides were computed before the other axis snapped; extend them so they cover the final box.
  const final: SnapGuide[] = guides.map((g) =>
    g.axis === 'x'
      ? { ...g, from: Math.min(g.from, moving.minY + dy), to: Math.max(g.to, moving.maxY + dy) }
      : { ...g, from: Math.min(g.from, moving.minX + dx), to: Math.max(g.to, moving.maxX + dx) },
  );
  return { delta: { x: dx, y: dy }, guides: final };
};

/** Translates bounds by a delta. */
export const shiftBounds = (b: Bounds, d: Vec2): Bounds => ({
  minX: b.minX + d.x,
  minY: b.minY + d.y,
  maxX: b.maxX + d.x,
  maxY: b.maxY + d.y,
});
